import { FC, useEffect, useRef, useState } from 'react';
import SignaturePad from '../../component/signaturePad/signaturePad';
import { clearSignature } from '../../utils/helper';
import { SignaturePadRef } from '../../component/signaturePad/signaturePad.type';
import { useUserDetailContext } from '../../service/context/userDetailContext';
import { Trans } from 'react-i18next';
import Button from '../../component/button/button';
import DropDownInput from '../../component/dropDownInput/dropDownInput';
import { GetUsersResponse, SweepSignature } from '@inplay/models';
import { isString } from 'lodash';
import { UserRole } from './sweeps.type';
import { SignatureModalProps } from './sweepReport.type';
import ButtonSpinner from '../../assets/buttonLoading/buttonLoading';
import { Option } from '../../component/dropDownInput/dropDownInput.type';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';

const SignatureModal: FC<SignatureModalProps> = (props) => {
  const { userDetail } = useUserDetailContext();
  const sigCanvasRef = useRef<SignaturePadRef>(null);
  const { isTablet } = useResponsiveLayout();
  const [signatureName, setSignatureName] = useState<string>('');
  const [selectedSigneeId, setSelectedSigneeId] = useState<string>('');
  const [signature, setSignature] = useState<string | undefined>(undefined);
  const [signatureId, setSignatureId] = useState<string | undefined>(undefined);
  const [isChecked, setIsChecked] = useState(!props.isBulk);

  const signNameOption: Option[] | undefined = props.usersList?.data.map(
    (user: GetUsersResponse) => ({
      label: `${user.firstName} ${user.lastName}`,
      value: user.id,
    }),
  );

  useEffect(() => {
    if (!props.isOpen) return;

    const isManager: boolean = props.type === UserRole.MANAGER;
    const isStaff: boolean = props.type === UserRole.STAFF;

    const userId: string = userDetail?.id ?? '';
    const userSignature: string = userDetail?.signature ?? '';
    const userFullName: string = `${userDetail?.firstName ?? ''} ${userDetail?.lastName ?? ''
      }`;

    const reviewedBy: SweepSignature | undefined =
      props.selectedSweepRowForModal?.reviewedBy;
    const reviewedSignature: string | undefined =
      reviewedBy?.signature ?? userDetail?.signature;
    const reviewedSignatureId: string | undefined = reviewedSignature
      ? `users/signature_${userId}.jpeg`
      : undefined;
    const shouldSetStaffSignature =
      isStaff && userId === selectedSigneeId && !!userSignature;

    if (isManager) {
      setSignatureName(userFullName);
      setSignature(reviewedSignature);
      setSignatureId(reviewedSignatureId);
    } else if (shouldSetStaffSignature) {
      setSignature(userSignature);
      setSignatureId(reviewedSignatureId);
      setSignatureName(userFullName);
      setSelectedSigneeId(userId);
    } else {
      setSignature('');
      setSignatureId(reviewedSignatureId);
      setSignatureName(signatureName);
      setSelectedSigneeId(selectedSigneeId);
    }
  }, [
    props.isOpen,
    props.type,
    props.selectedSweepRowForModal,
    userDetail,
    selectedSigneeId,
  ]);

  if (!props.isOpen) return null;

  const handleDeleteSignature = () => {
    clearSignature(sigCanvasRef);
    setSignature('');
  };

  const handleSave = () => {
    props.handleAddEditSignature({
      firstName: signatureName.split(' ')[0] || '',
      lastName: signatureName.split(' ')[1] || '',
      _id: selectedSigneeId || userDetail?.id || '',
      signature: signature ?? '',
      signatureId: signatureId ?? undefined,
      signedDate: new Date(),
      notes: props.signatureNotes,
    });
  };

  const isStaffSaveDisabled: boolean = !signature || !selectedSigneeId;
  const isManagerSaveDisabled: boolean = !signature || !signatureName.trim();

  const handleApprovalConfirmation = () => {
    setIsChecked(!isChecked);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div
        className={`bg-white rounded-xl p-6 pb-0 shadow-lg relative overflow-auto ${isTablet
            ? 'h-[90vh] w-[85vw] flex flex-col pb-8'
            : 'w-full max-w-2xl h-[470px]'
          }`}
      >
        <h2 className="text-lg font-semibold mb-4">
          {props.isEdit ? (
            <Trans i18nKey="sweeps.editSignature" />
          ) : (
            <Trans i18nKey="sweeps.addSignature" />
          )}
        </h2>

        <label className="text-sm font-medium mb-1 block text-[#1A1A1A]">
          <Trans i18nKey="sweeps.signeeName" />
        </label>

        {props.type === UserRole.MANAGER ? (
          <input
            type="text"
            placeholder="Enter Name"
            className="w-full bg-[#FAFAFA] rounded-full px-4 py-2 focus:outline-none"
            value={signatureName}
            disabled={true}
            onChange={(e) => setSignatureName(e.target.value)}
          />
        ) : (
          <DropDownInput
            options={signNameOption}
            selected={selectedSigneeId}
            onSelect={(value: string | string[]) => {
              if (isString(value)) {
                setSelectedSigneeId(value);

                const selectedUser = props.usersList?.data.find(
                  (user) => user.id === value,
                );
                if (selectedUser) {
                  setSignatureName(
                    `${selectedUser.firstName} ${selectedUser.lastName}`,
                  );
                }

                if (selectedUser?.id === userDetail?.id) {
                  setSignature(userDetail?.signature);
                } else {
                  clearSignature(sigCanvasRef);
                  setSignature('');
                }
              }
            }}
            placeholder="Enter name or select"
            className="w-[625px]"
          />
        )}

        {props.isBulk && (
          <div className="flex flex-nowrap mt-2">
            <div className="mt-1">
              <input
                type="checkbox"
                onChange={handleApprovalConfirmation}
                className="mr-3 w-4 h-4 accent-violet-700"
              />
            </div>
            <div className="text-sm font-semibold">
              <Trans i18nKey="sweeps.approveConfirmation" />
            </div>
          </div>
        )}

        <div className={`mt-5 ${isTablet && 'flex-grow'}`}>
          <SignaturePad
            ref={sigCanvasRef}
            onDelete={handleDeleteSignature}
            initialSignature={signature}
            handleDrawEnd={() =>
              setSignature(sigCanvasRef?.current?.getTrimmedDataURL())
            }
            style={isTablet ? { height: '100%' } : {}}
          />
        </div>

        <div className="flex justify-between items-center mt-4">
          <Button
            onClick={props.onClose}
            className="w-[45%] py-2 border rounded-full text-[#1A1A1A] border-[#CFCFCF] bg-white hover:text-[#1A1A1A] hover:bg-white hover:border-[#CFCFCF]"
          >
            <Trans i18nKey="universalStaffLoginModal.cancel" />
          </Button>

          <Button
            disabled={
              !isChecked || props.type === UserRole.STAFF
                ? isStaffSaveDisabled
                : isManagerSaveDisabled
            }
            className="w-[45%] py-2  rounded-full text-white border-[#CFCFCF]"
            onClick={handleSave}
          >
            {props.loading ? (
              <ButtonSpinner />
            ) : (
              <Trans i18nKey="universalStaffLoginModal.save" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SignatureModal;
